import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "../../store/store";

import { useTranslation, Trans } from "react-i18next";
import useCalendar from "../../calendar/context/useCalendar";

import {
    getCalendarData,
    getOriginalData,
    setShowChangeList,
    initData,
} from "../../store/data";
import { HeaderMainWrapper } from "./StyledComponents";
import Button from "@mui/material/Button";
import { grey } from "@mui/material/colors";

import { GridColDef, DataGrid } from "@mui/x-data-grid";
import { deDE, huHU, roRO, esES } from "@mui/x-data-grid/locales";
import {
    arraysEqual,
    isEven,
    getDateFromWeek,
    isEmptyObject,
    hasKey,
    filterWeekSettingsData,
} from "../utils";

import { Consultation, Week } from "../../calendar/model";

const sort = (week: Week) => {
    const sorted: Week = {};

    Object.keys(week).forEach((day) => {
        sorted[day] = week[parseInt(day)]
            ?.slice()
            .sort((a: Consultation, b: Consultation) => {
                if (a.start < b.start) {
                    return -1;
                }
                if (a.start > b.start) {
                    return 1;
                }

                return 0;
            });
    });

    return sorted;
};

function filterObjectByKey(inputObj: any, minKey: any) {
    // Convert the input object keys into numbers and filter them
    let filteredObj = Object.keys(inputObj)
        .filter((key) => Number(key) >= minKey) // Keep keys >= minKey
        .reduce((acc: any, key) => {
            acc[key] = inputObj[key]; // Add the matching key-value pairs to a new object
            return acc;
        }, {});

    return filteredObj;
}

const getTexts = (lng: string) => {
    switch (lng) {
        case "hu":
            return {
                ...huHU.components.MuiDataGrid.defaultProps.localeText,
                filterOperatorDoesNotContain: "nem tartalmazza:",
                filterOperatorDoesNotEqual: "nem egyenlő ezzel:",
            };
        case "de":
            return deDE.components.MuiDataGrid.defaultProps.localeText;
        case "ro":
            return roRO.components.MuiDataGrid.defaultProps.localeText;
        case "es":
            return esES.components.MuiDataGrid.defaultProps.localeText;
        default:
            return undefined;
    }
};

interface Props {
    closeModal: () => void;
}

const ChangeList = ({ closeModal }: Props) => {
    // const [paginationModel, setPaginationModel] = useState({
    //     pageSize: 50,
    //     page: 0,
    // });

    const { t } = useTranslation();

    const {
        profile: { doctorName },
        inputCallbacks,
    } = useCalendar();

    const dispatch = useDispatch();

    const calendarData = useSelector(getCalendarData);
    const originalData = useSelector(getOriginalData);

    const [checkData, setCheckData] = useState<any | null>(null);
    const [listData, setListData] = useState<any[]>([]);
    const [selected, setSelected] = useState<any>([]);

    useEffect(() => {
        setCheckData(getCheckData(originalData.individualWeekSettings));
    }, []);

    const getCheckData = (individualWeekSettings: any) => {
        function getISOWeekNumber(date: any) {
            const target = new Date(date);
            target.setDate(target.getDate() - ((target.getDay() + 6) % 7) + 3); // Move to the nearest Thursday
            const firstThursday = new Date(target.getFullYear(), 0, 4);
            const diff = target.getTime() - firstThursday.getTime();
            const weekNumber = 1 + Math.round(diff / (7 * 86400000));
            return weekNumber;
        }

        const getCurrentWeek = function (date = new Date()) {
            return getISOWeekNumber(date);
        };

        let currentYear = new Date().getFullYear();
        let currentWeek = getCurrentWeek();

        let _individualWeekSettings = individualWeekSettings;

        let thisYearData: any = {};
        let nextYearData: any = {};
        let thisYearWeeksNumber: any;

        for (const [index, year] of Object.entries(_individualWeekSettings)) {
            if (index.toString() == currentYear.toString()) {
                let yearData = _individualWeekSettings[`${index}`];

                for (let week in yearData) {
                    let _weekData = yearData[week];

                    let dayOfWeek = new Date().getDay();
                    let actualDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

                    if (week.toString() == currentWeek.toString()) {
                        _weekData = filterObjectByKey(_weekData, actualDay);
                    }

                    if (+week >= currentWeek) {
                        thisYearData[week] = _weekData;
                    }
                }
            }
            thisYearWeeksNumber = Object.keys(thisYearData).length;

            if (index.toString() == (currentYear + 1).toString()) {
                let yearData = _individualWeekSettings[`${index}`];

                let nextYearWeeksNumber = 52 - thisYearWeeksNumber;

                for (let week in yearData) {
                    if (+week <= nextYearWeeksNumber) {
                        nextYearData[week] = yearData[week];
                    }
                }
            }
        }

        let updatedIndividualWeekSettings: any = {};
        updatedIndividualWeekSettings[currentYear] = thisYearData;
        updatedIndividualWeekSettings[currentYear + 1] = nextYearData;

        // return updatedIndividualWeekSettings;

        let newIndividual: any = {};
        Object.keys(updatedIndividualWeekSettings).forEach((year) => {
            if (!newIndividual[year]) {
                newIndividual[year] = {};
            }

            Object.keys(updatedIndividualWeekSettings[year]).forEach((week) => {
                newIndividual[year][week] = sort(
                    updatedIndividualWeekSettings[year][week]
                );
            });
        });

        return newIndividual;
    };

    const checkDifference = (regularDayData: any, dayData: any) => {
        if (!dayData) {
            return false;
        }
        let difference = false;
        if (regularDayData.length != dayData.length) {
            difference = true;
        } else {
            regularDayData.forEach((rd: any, index: number) => {
                let _dayData = dayData[index];
                if (
                    rd.end != _dayData.end ||
                    rd.start != _dayData.start ||
                    rd.room_id != _dayData.room_id
                ) {
                    difference = true;
                }
                if (
                    !arraysEqual(
                        rd.specialization_id,
                        _dayData.specialization_id
                    )
                ) {
                    difference = true;
                }
            });
        }
        return difference;
    };

    const columns: GridColDef[] = [
        {
            field: "date",
            headerName: t("date")!,
            flex: 1,
            filterable: false,
            sortable: true,
            disableColumnMenu: true,
            renderCell: (data: any) => {
                return (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                        }}
                    >
                        {data.row.date}
                    </div>
                );
            },
        },
        {
            field: "week",
            headerName: t("week_upper")!,
            flex: 1,
            filterable: false,
            sortable: true,
            disableColumnMenu: true,
            renderCell: (data: any) => {
                return (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                        }}
                    >{`${data.row.week}.`}</div>
                );
            },
            valueGetter: (data, row) => {
                return row.week;
            },
        },
        {
            field: "day",
            headerName: t("day")!,
            flex: 1,
            filterable: false,
            sortable: true,
            disableColumnMenu: true,
            renderCell: (data: any) => {
                return (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                        }}
                    >
                        {dayHelper[data.row.day]}
                    </div>
                );
            },
            valueGetter: (data, row) => {
                return dayHelper[row.day];
            },
        },
        {
            field: "intervals",
            headerName: "Naptár nyitás - zárás",
            flex: 1,
            filterable: false,
            sortable: true,
            disableColumnMenu: true,
            renderCell: (data: any) => {
                if (!data.row.data) {
                    return;
                }
                if (data.row.data.length == 1) {
                    let start = data.row.data[0].start;
                    let end = data.row.data[0].end;
                    return (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                height: "100%",
                            }}
                        >{`${start} - ${end}`}</div>
                    );
                } else {
                    let _datas = data.row.data;
                    return (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                height: "100%",
                            }}
                        >
                            {_datas.map((d: any, index: number) => {
                                return (
                                    <p key={index}>{`${d.start} - ${d.end}`}</p>
                                );
                            })}
                        </div>
                    );
                }
            },
            valueGetter: (data, row) => {
                if (!row.data) {
                    return;
                }
                if (row.data.length == 1) {
                    return `${row.data[0].start} - ${row.data[0].end}`;
                } else {
                    let dataString = "";
                    row.data.forEach((d: any) => {
                        dataString += `${d.start}${d.end}`;
                    });
                    return dataString;
                }
            },
        },
        {
            field: "specialization",
            headerName: t("specialization")!,
            flex: 1,
            filterable: false,
            sortable: true,
            disableColumnMenu: true,
            renderCell: (data: any) => {
                let allSpecializations = calendarData.specializations;
                if (!data.row.data) {
                    return;
                }
                if (data.row.data.length == 1) {
                    let _specializations = data.row.data[0].specialization_id;

                    if (_specializations.length == 0) {
                        return (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                }}
                            >
                                -
                            </div>
                        );
                    }

                    return (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                height: "100%",
                            }}
                        >
                            {_specializations.map((s: any, index: number) => {
                                let spec = allSpecializations.find(
                                    (specData: any) => {
                                        return specData.specialization_id == s;
                                    }
                                );
                                if (spec) {
                                    return (
                                        <p key={index}>{spec.specialization}</p>
                                    );
                                } else {
                                    return "-";
                                }
                            })}
                        </div>
                    );
                } else {
                    let _specializations: number[] = [];
                    let _datas = data.row.data;
                    _datas.forEach((d: any) => {
                        _specializations = [
                            ..._specializations,
                            ...d.specialization_id,
                        ];
                    });

                    const uniqueSpecializations = [
                        ...new Set(_specializations),
                    ];
                    return (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                height: "100%",
                            }}
                        >
                            {uniqueSpecializations.map(
                                (s: any, index: number) => {
                                    let spec = allSpecializations.find(
                                        (specData: any) => {
                                            return (
                                                specData.specialization_id == s
                                            );
                                        }
                                    );
                                    if (spec) {
                                        return (
                                            <p key={index}>
                                                {spec.specialization}
                                            </p>
                                        );
                                    } else {
                                        return "-";
                                    }
                                }
                            )}
                        </div>
                    );
                }
            },
            valueGetter: (data, row) => {
                let dataString = "";
                let allSpecializations = calendarData.specializations;
                if (!row.data) {
                    return;
                }
                if (row.data.length == 1) {
                    let _specializations = row.data[0].specialization_id;
                    _specializations.forEach((s: number) => {
                        let spec = allSpecializations.find((specData: any) => {
                            return specData.specialization_id == s;
                        });
                        if (spec) {
                            dataString += spec.specialization;
                        }
                    });
                    return dataString;
                } else {
                    let _specializations: number[] = [];
                    let _datas = row.data;
                    _datas.forEach((d: any) => {
                        _specializations = [
                            ..._specializations,
                            ...d.specialization_id,
                        ];
                    });

                    const uniqueSpecializations = [
                        ...new Set(_specializations),
                    ];

                    uniqueSpecializations.forEach((s: number) => {
                        let spec = allSpecializations.find((specData: any) => {
                            return specData.specialization_id == s;
                        });
                        if (spec) {
                            dataString += spec.specialization;
                        }
                    });
                    return dataString;
                }
            },
        },
        {
            field: "room",
            headerName: t("room")!,
            flex: 1,
            filterable: false,
            sortable: true,
            disableColumnMenu: true,
            renderCell: (data: any) => {
                let allRooms = calendarData.rooms;
                if (!data.row.data) {
                    return;
                }
                if (data.row.data.length == 1) {
                    let roomId = data.row.data[0].room_id;

                    if (!roomId || roomId == "-1") {
                        return (
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    height: "100%",
                                }}
                            >
                                -
                            </div>
                        );
                    }

                    let roomData = allRooms.find((r) => r.id == roomId);

                    if (roomData) {
                        return (
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    height: "100%",
                                }}
                            >
                                {roomData.name}
                            </div>
                        );
                    }
                } else {
                    let _rooms: string[] = [];
                    let _datas = data.row.data;
                    _datas.forEach((d: any) => {
                        if (d.room_id && d.room_id != "-1") {
                            _rooms.push(d.room_id);
                        }
                    });
                    const uniqueRooms = [...new Set(_rooms)];

                    return (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                height: "100%",
                            }}
                        >
                            {uniqueRooms.map((r: any, index: number) => {
                                let room = allRooms.find((roomData: any) => {
                                    return roomData.id == r;
                                });
                                if (room) {
                                    return <p key={index}>{room.name}</p>;
                                } else {
                                    return "-";
                                }
                            })}
                        </div>
                    );
                }
            },
            valueGetter: (data, row) => {
                let allRooms = calendarData.rooms;
                let dataString = "";
                if (!row.data) {
                    return;
                }

                if (row.data.length == 1) {
                    let roomId = row.data[0].room_id;

                    let roomData = allRooms.find((r) => r.id == roomId);

                    if (roomData) {
                        dataString += roomData.name;
                    }
                } else {
                    let _rooms: string[] = [];
                    let _datas = row.data;
                    _datas.forEach((d: any) => {
                        if (d.room_id && d.room_id != "-1") {
                            _rooms.push(d.room_id);
                        }
                    });
                    const uniqueRooms = [...new Set(_rooms)];

                    uniqueRooms.map((r: any, index: number) => {
                        let room = allRooms.find((roomData: any) => {
                            return roomData.id == r;
                        });
                        if (room) {
                            dataString += room.name;
                        }
                    });
                }

                return dataString;
            },
        },
    ];

    const dayHelper: any = {
        0: t("Monday"),
        1: t("Tuesday"),
        2: t("Wednesday"),
        3: t("Thursday"),
        4: t("Friday"),
        5: t("Saturday"),
        6: t("Sunday"),
    };

    useEffect(() => {
        if (checkData) {
            let dataToShow = [];
            let regularSettings: any = calendarData.regularWeekSettings;

            for (const year in calendarData.individualWeekSettings) {
                const weeks = calendarData.individualWeekSettings[year];
                for (const week in weeks) {
                    const days = weeks[week];

                    if (isEmptyObject(days)) {
                        if (checkData[year][week]) {
                            let _days = checkData[year][week];

                            if (!_days) {
                                return;
                            }

                            for (const day in _days) {
                                dataToShow.push({
                                    id: `${year}-${week}-${day}`,
                                    date: getDateFromWeek(+year, +week, +day),
                                    year: year,
                                    week: week,
                                    day: day,
                                    data: checkData[year][week][day],
                                });
                            }
                        }
                    } else {
                        let _days = checkData[year][week];
                        if (_days) {
                            for (const _day in _days) {
                                if (!hasKey(days, _day)) {
                                    dataToShow.push({
                                        id: `${year}-${week}-${_day}`,
                                        date: getDateFromWeek(
                                            +year,
                                            +week,
                                            +_day
                                        ),
                                        year: year,
                                        week: week,
                                        day: _day,
                                        data: checkData[year][week][_day],
                                    });
                                }
                            }
                        }
                    }

                    for (const day in days) {
                        const dayData = days[day];

                        let _data: any = {};

                        let regularDayData = regularSettings.even[day];
                        if (regularSettings.oddWeekUsed) {
                            if (!isEven(+week)) {
                                regularDayData = regularSettings.odd[day];
                            }
                        }

                        if (
                            !checkData[year][week] ||
                            !checkData[year][week][day]
                        ) {
                            _data = {
                                id: `${year}-${week}-${day}`,
                                date: getDateFromWeek(+year, +week, +day),
                                year: year,
                                week: week,
                                day: day,
                                data: dayData,
                            };
                            dataToShow.push(_data);
                        } else {
                            _data = {
                                id: `${year}-${week}-${day}`,
                                date: getDateFromWeek(+year, +week, +day),
                                year: year,
                                week: week,
                                day: day,
                                data: checkData[year][week][day],
                            };
                        }
                        if (!regularDayData) {
                            if (_data.data) {
                                dataToShow.push({
                                    ..._data,
                                    emptyOnChange: true,
                                });
                            }
                        } else {
                            if (checkDifference(regularDayData, _data.data)) {
                                if (_data.data) {
                                    dataToShow.push(_data);
                                }
                            }
                        }
                    }
                }
            }

            let _dataToShow = dataToShow.filter(
                (d) =>
                    new Date(d.date).setHours(0, 0, 0, 0) >=
                    new Date().setHours(0, 0, 0, 0)
            );
            setListData(_dataToShow);

            // for (const year in checkData) {
            //     const weeks = checkData[year];
            //     for (const week in weeks) {
            //         const days = weeks[week];
            //         for (const day in days) {
            //             const dayData = days[day];

            //             let regularDayData = regularSettings.even[day];
            //             if (regularSettings.oddWeekUsed) {
            //                 if (!isEven(+week)) {
            //                     regularDayData = regularSettings.odd[day];
            //                 }
            //             }

            //             let _data = {
            //                 id: `${year}-${week}-${day}`,
            //                 date: getDateFromWeek(+year, +week, +day),
            //                 year: year,
            //                 week: week,
            //                 day: day,
            //                 data: dayData,
            //             };
            //             if (!regularDayData) {
            //                 if (_data.data) {
            //                     dataToShow.push({
            //                         ..._data,
            //                         emptyOnChange: true,
            //                     });
            //                 }
            //             } else {
            //                 if (checkDifference(regularDayData, dayData)) {
            //                     if (_data.data) {
            //                         dataToShow.push(_data);
            //                     }
            //                 }
            //             }
            //         }
            //     }
            // }

            // setListData(dataToShow);
        }
    }, [checkData]);

    const handleUpdate = () => {
        let originalIndividualData = JSON.parse(
            JSON.stringify(originalData.individualWeekSettings)
        );
        let sendData = JSON.parse(JSON.stringify(calendarData));

        let modifiedIndividualData = JSON.parse(
            JSON.stringify(calendarData.individualWeekSettings)
        );

        listData.forEach((l) => {
            let _selected = selected.find((s: string) => s == l.id);

            // Ensure all parent objects exist before accessing nested properties

            if (_selected) {
                if (l.emptyOnChange) {
                    originalIndividualData[l.year][l.week][l.day] = null;
                } else {
                    if (!originalIndividualData[l.year]) {
                        originalIndividualData[l.year] = {};
                    }
                    if (!originalIndividualData[l.year][l.week]) {
                        originalIndividualData[l.year][l.week] = {};
                    }

                    originalIndividualData[l.year][l.week][l.day] =
                        modifiedIndividualData[l.year][l.week][l.day];
                }
            }

            // if (!_selected) {
            //     // Check if original data exists before copying
            //     if (originalIndividualData[l.year]?.[l.week]?.[l.day]) {
            //         modifiedIndividualData[l.year][l.week][l.day] =
            //             originalIndividualData[l.year][l.week][l.day];
            //     } else {
            //         modifiedIndividualData[l.year][l.week][l.day] = null;
            //     }
            // } else {
            //     if (l.emptyOnChange) {
            //         modifiedIndividualData[l.year][l.week][l.day] = null;
            //     }
            // }
        });

        sendData["individualWeekSettings"] = filterWeekSettingsData(
            originalIndividualData
        );
        dispatch(initData(sendData));
        inputCallbacks.onSaveCalendar(sendData);
        closeModal();
    };

    return (
        <>
            <HeaderMainWrapper>
                <h3
                    style={{
                        margin: "1rem auto",
                        fontSize: "1.25rem",
                        fontFamily: "Titillium Web, sans-serif",
                    }}
                >
                    {t("attention")}
                </h3>
                <p
                    style={{
                        fontFamily: "Titillium Web, sans-serif",
                        marginBottom: "0 !important",
                    }}
                >
                    <Trans
                        i18nKey="doctor_reservation_modify"
                        components={{
                            span: <span style={{ fontWeight: "bold" }} />,
                        }}
                        values={{ doctor: doctorName }}
                    />
                </p>
                <p
                    style={{
                        fontFamily: "Titillium Web, sans-serif",
                        marginBottom: "0 !important",
                    }}
                >
                    {t("modify_text")}
                </p>
            </HeaderMainWrapper>
            <div style={{ padding: "0 1rem", paddingBottom: "80px" }}>
                <DataGrid
                    localeText={getTexts(
                        localStorage.getItem("i18nextLng") || ""
                    )}
                    rows={listData}
                    columns={columns}
                    checkboxSelection
                    sx={{
                        fontFamily: "'Titillium Web', sans-serif",
                        border: "none",
                        "& .MuiTablePagination-selectLabel": {
                            margin: "0",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            margin: "0",
                        },
                        // "& .MuiDataGrid-columnHeaderTitleContainer": {
                        //     justifyContent: "center",
                        // },
                        "& .MuiCheckbox-root": {
                            transform: "scale(0.8)", // manually shrink
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontWeight: "bold", // This line makes column titles bold
                        },
                        height: "50vh",
                    }}
                    getRowHeight={() => "auto"}
                    disableColumnFilter
                    disableColumnSelector
                    disableDensitySelector
                    ignoreDiacritics
                    disableVirtualization
                    showToolbar
                    slotProps={{
                        toolbar: {
                            printOptions: { disableToolbarButton: true },
                            csvOptions: { disableToolbarButton: true },
                        },
                    }}
                    onRowSelectionModelChange={(value) => {
                        let selected = [...value.ids];
                        setSelected(selected);
                    }}
                    // pagination
                    // paginationModel={paginationModel}
                    // onPaginationModelChange={setPaginationModel}
                    // pageSizeOptions={[10, 25, 50]}
                />
            </div>
            {/* Floating button container */}
            <div
                style={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: "1rem",
                    display: "flex",
                    justifyContent: "flex-end",
                    zIndex: 1000,
                }}
            >
                <Button
                    onClick={() => dispatch(setShowChangeList(false))}
                    variant="contained"
                    sx={{
                        backgroundColor: grey[500],
                        color: "#fff",
                        "&:hover": {
                            backgroundColor: grey[600],
                        },
                        marginRight: "1rem",
                        fontFamily: "Titillium Web, sans-serif",
                    }}
                >
                    {t("back")}
                </Button>
                <Button
                    onClick={() => handleUpdate()}
                    disabled={selected.length == 0}
                    variant="contained"
                    sx={{
                        fontFamily: "Titillium Web, sans-serif",
                        // backgroundColor: "#2f9df8",
                        // color: "#fff",
                        // "&:hover": {
                        //     backgroundColor: "#1d8ee5",
                        // },
                    }}
                >
                    {t("save")}
                </Button>
            </div>
        </>
    );
};

export default ChangeList;
